/**
 * Unified User Profile Hook
 * Combines auth user, profile data, and roles into a single easy-to-use hook
 * with proper caching via React Query
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@xanectech/supabase-client';
import type { User } from '@supabase/supabase-js';
import {
  AppRole,
  UserRole,
  RolePermissions,
  ROLE_PERMISSIONS,
  UserProfileData,
} from '@xanectech/types';
import { createLogger, isRecord } from '@xanectech/lib';
import { useMemo, useCallback } from 'react';

const logger = createLogger('Auth - useUserProfile');

interface UserRoleWithCompany extends UserRole {
  divisionName?: string | null;
  companyName?: string | null;
  companyLogoUrl?: string | null;
}

interface UserMetadata {
  first_name?: string;
  last_name?: string;
}

const readString = (
  value: Record<string, unknown>,
  key: string,
): string | undefined =>
  typeof value[key] === 'string' ? value[key] : undefined;

const readNullableString = (
  value: Record<string, unknown>,
  key: string,
): string | null | undefined => {
  const field = value[key];
  if (field === null) {
    return null;
  }
  return typeof field === 'string' ? field : undefined;
};

const readBoolean = (
  value: Record<string, unknown>,
  key: string,
): boolean | undefined =>
  typeof value[key] === 'boolean' ? value[key] : undefined;

const APP_ROLES: AppRole[] = [
  'super_admin',
  'internal_admin',
  'company_admin',
  'data_administrator',
  'viewer',
  'end_user',
];

const isAppRole = (value: unknown): value is AppRole =>
  typeof value === 'string' && APP_ROLES.includes(value as AppRole);

const getUserMetadata = (value: unknown): UserMetadata => {
  if (!isRecord(value)) {
    return {};
  }

  return {
    first_name:
      typeof value.first_name === 'string' ? value.first_name : undefined,
    last_name:
      typeof value.last_name === 'string' ? value.last_name : undefined,
  };
};

const mapBaseUserRole = (row: unknown): UserRole | null => {
  if (!isRecord(row)) {
    return null;
  }

  const id = readString(row, 'id');
  const user_id = readString(row, 'user_id');
  const roleValue = row.role;

  if (!id || !user_id || !isAppRole(roleValue)) {
    return null;
  }

  const nowIso = new Date().toISOString();

  return {
    id,
    user_id,
    role: roleValue,
    assigned_by: readString(row, 'assigned_by'),
    assigned_at: readString(row, 'assigned_at') || nowIso,
    company_id: readString(row, 'company_id'),
    division_id: readString(row, 'division_id'),
    is_active: readBoolean(row, 'is_active') ?? true,
    created_at: readString(row, 'created_at') || nowIso,
    updated_at: readString(row, 'updated_at') || nowIso,
  };
};

const mapDivisionUserRole = (row: unknown): UserRoleWithCompany | null => {
  const baseRole = mapBaseUserRole(row);
  if (!baseRole || !isRecord(row)) {
    return null;
  }

  const division = isRecord(row.company_divisions)
    ? row.company_divisions
    : undefined;
  const company = isRecord(row.companies) ? row.companies : undefined;

  return {
    ...baseRole,
    divisionName: division
      ? (readNullableString(division, 'name') ?? null)
      : null,
    companyName: company ? (readNullableString(company, 'name') ?? null) : null,
    companyLogoUrl: company
      ? (readNullableString(company, 'logo_url') ?? null)
      : null,
  };
};

/**
 * Combined user data from all sources
 */
export interface UnifiedUserProfile {
  // Auth user info
  userId: string;
  email: string;
  emailVerified: boolean;

  // Profile info
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  fullName: string;
  phoneNumber: string | null;
  photoUrl: string | null;
  companyId: string | null;
  isActive: boolean;
  webauthnEnabled: boolean;
  profileUrl?: string | null;
  country_id?: string | null;
  country_name?: string | null;
  country_emoji?: string | null;
  country_dialing_code?: string | null;

  // Preferences
  preferences: {
    expiry?: string;
    theme?: 'light' | 'dark' | 'system';
    [key: string]: unknown;
  };

  // Role info
  roles: UserRole[];
  primaryRole: AppRole;
  permissions: RolePermissions;

  // Company Info
  companyName?: string | null;
  companyLogoUrl?: string | null;

  hasRole: (role: AppRole) => boolean;
  hasPermission: (permission: keyof RolePermissions) => boolean;
  isAdmin: boolean;
  isInternalAdmin: boolean;
  isSuperAdmin: boolean;
  isCompanyAdmin: boolean;
  isEndUser: boolean;
  isDataAdministrator: boolean;
  isViewer: boolean;
}

/**
 * Hook return type
 */
export interface UseUserProfileReturn {
  profile: UnifiedUserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  getProfile: () => Promise<UnifiedUserProfile | null>;
}

// Query keys for caching
const PROFILE_QUERY_KEY = 'user-profile';
const ROLES_QUERY_KEY = 'user-roles';

/**
 * Fetch user profile from user_profiles table
 */
async function fetchUserProfile(
  userId: string,
): Promise<UserProfileData | null> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // No profile found - not necessarily an error
      return null;
    }
    throw error;
  }

  const profileData = data as UserProfileData;

  if (profileData.country_id) {
    const { data: countryData, error: countryError } = await supabase
      .from('geo_countries')
      .select('name, phone_code, emoji')
      .eq('id', profileData.country_id)
      .single();

    if (countryError) {
      logger.error(
        'Failed to fetch country data',
        { error: countryError },
        'auth',
      );
    } else if (countryData) {
      profileData.country_name = countryData.name;
      profileData.country_dialing_code = countryData.phone_code;
      profileData.country_emoji = countryData.emoji;
    }
  }

  return profileData;
}

/**
 * Fetch user roles from user_roles table
 */
async function fetchUserRoles(userId: string): Promise<UserRoleWithCompany[]> {
  const { data, error } = await supabase
    .from('user_roles')
    .select(
      'id, user_id, role, assigned_by, assigned_at, company_id, is_active, created_at, updated_at',
    )
    .eq('user_id', userId)
    .eq('is_active', true);

  if (error) {
    throw error;
  }
  const roles = (Array.isArray(data) ? data : [])
    .map(mapBaseUserRole)
    .filter((role): role is UserRoleWithCompany => role !== null);

  // Get company division roles for the user
  const { data: divisionRoles, error: divisionError } = await supabase
    .from('user_division_roles')
    .select(
      '*, company_divisions (name, description), companies (name, logo_url)',
    )
    .eq('user_id', userId)
    .eq('is_active', true);

  if (divisionError) {
    throw divisionError;
  }

  if (divisionRoles && divisionRoles.length > 0) {
    // Merge division roles into main roles array
    const mergedRoles = [
      ...roles,
      ...divisionRoles
        .map(mapDivisionUserRole)
        .filter((role): role is UserRoleWithCompany => role !== null),
    ];

    return mergedRoles;
  }

  return roles;
}

async function fetchCountryData(countryId: string | null | undefined): Promise<{
  country_name: string;
  dialing_code: string | null;
  emoji: string | null;
} | void> {
  if (!countryId) {
    return;
  }

  try {
    const country = await supabase
      .from('geo_countries')
      .select('name, phone_code, emoji')
      .eq('id', countryId)
      .single()
      .then(({ data, error }) => {
        if (error) {
          throw error;
        }
        return data;
      });

    if (country) {
      return {
        country_name: country.name,
        dialing_code: country.phone_code,
        emoji: country.emoji,
      };
    }
  } catch (error) {
    logger.error(
      'Failed to fetch country data',
      { error: error instanceof Error ? error.message : String(error) },
      'components',
    );
  }
}

/**
 * Get primary role from list of roles (highest privilege)
 */
function getPrimaryRole(roles: UserRole[]): AppRole {
  if (roles.length === 0) return 'end_user';

  const roleHierarchy: Record<AppRole, number> = {
    super_admin: 1,
    internal_admin: 2,
    company_admin: 3,
    data_administrator: 4,
    viewer: 5,
    end_user: 6,
  };

  const primaryRole = roles.reduce((highest, current) => {
    return roleHierarchy[current.role as AppRole] <
      roleHierarchy[highest.role as AppRole]
      ? current
      : highest;
  });

  return primaryRole.role as AppRole;
}

/**
 * Unified hook for accessing user profile, auth, and roles
 * Profile data is NOT automatically fetched - call fetchProfile() to load data
 */
export function useUserProfile(): UseUserProfileReturn {
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();

  const userId = user?.id;
  const isAuthenticated = !!user;

  // Fetch profile data with React Query (auto-fetch when authenticated)
  const {
    data: profileData,
    isLoading: profileLoading,
    error: profileError,
  } = useQuery({
    queryKey: [PROFILE_QUERY_KEY, userId],
    queryFn: () => fetchUserProfile(userId!),
    enabled: isAuthenticated && !!userId, // Auto-fetch when user is authenticated
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Fetch roles data with React Query (auto-fetch when authenticated)
  const {
    data: rolesData,
    isLoading: rolesLoading,
    error: rolesError,
  } = useQuery({
    queryKey: [ROLES_QUERY_KEY, userId],
    queryFn: () => fetchUserRoles(userId!),
    enabled: isAuthenticated && !!userId, // Auto-fetch when user is authenticated
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Fetch country information if available
  const countryId =
    profileData &&
    typeof profileData === 'object' &&
    'country_id' in profileData
      ? (profileData as UserProfileData).country_id
      : undefined;

  const {
    country_name,
    dialing_code,
    emoji: country_emoji,
  } = useQuery({
    queryKey: ['country-info', countryId],
    queryFn: async () => fetchCountryData(String(countryId)),
    enabled: isAuthenticated && !!countryId,
  }).data || {};

  // Combined loading state
  const isLoading =
    authLoading || (isAuthenticated && (profileLoading || rolesLoading));

  // Combined error
  const error = profileError || rolesError || null;

  // Manual fetch function (memoized to prevent reference changes)
  const fetchProfile =
    useCallback(async (): Promise<UnifiedUserProfile | null> => {
      if (!userId) return null;

      try {
        const [profile, roles] = await Promise.all([
          queryClient.fetchQuery({
            queryKey: [PROFILE_QUERY_KEY, userId],
            queryFn: () => fetchUserProfile(userId),
          }),
          queryClient.fetchQuery({
            queryKey: [ROLES_QUERY_KEY, userId],
            queryFn: () => fetchUserRoles(userId),
          }),
        ]);

        return buildUnifiedProfile(user, profile, roles as UserRole[]);
      } catch (error) {
        logger.error('Error fetching user profile or roles:', {
          error: error instanceof Error ? error.message : String(error),
        });
        return null;
        // throw err;
      }
    }, [userId, queryClient, user]);

  // Refetch function (memoized)
  const refetch = useCallback(async () => {
    if (userId) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [PROFILE_QUERY_KEY, userId],
        }),
        queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY, userId] }),
      ]);
    }
  }, [userId, queryClient]);

  // Build unified profile
  let profile: UnifiedUserProfile | null = null;

  if (user && profileData && rolesData) {
    profile = buildUnifiedProfile(user, profileData, rolesData);
  }

  // Memoize return object to prevent infinite loops when used in dependency arrays
  return useMemo(
    () => ({
      profile,
      isLoading,
      isAuthenticated,
      error: error as Error | null,
      refetch,
      getProfile: fetchProfile,
    }),
    [profile, isLoading, isAuthenticated, error, refetch, fetchProfile],
  );
}

/**
 * Helper function to build unified profile from user, profile data, and roles
 */
function buildUnifiedProfile(
  user: User,
  profileData: UserProfileData | null,
  rolesData: UserRoleWithCompany[],
): UnifiedUserProfile {
  const roles = rolesData || [];
  const primaryRole = getPrimaryRole(roles);
  const permissions = ROLE_PERMISSIONS[primaryRole];
  const userMetadata = getUserMetadata(user.user_metadata);

  const hasRole = (role: AppRole): boolean => {
    return roles.some((r) => r.role === role);
  };

  const hasPermission = (permission: keyof RolePermissions): boolean => {
    return permissions[permission];
  };

  // Get name from profile or auth metadata
  const firstName = profileData?.first_name || userMetadata.first_name || null;
  const lastName = profileData?.last_name || userMetadata.last_name || null;
  const displayName = profileData?.display_name || null;

  // Build full name
  let fullName = displayName || '';
  if (!fullName && (firstName || lastName)) {
    fullName = [firstName, lastName].filter(Boolean).join(' ');
  }
  if (!fullName) {
    const emailPrefix = user.email?.split('@')[0];
    fullName = emailPrefix || 'User';
  }

  // Get company information if available
  const companyRole = roles.find((role) => role.company_id && role.division_id);
  const companyName = companyRole?.companyName || null;
  const companyLogoUrl = companyRole?.companyLogoUrl || null;

  return {
    // Auth user info
    userId: user.id,
    email: user.email || '',
    emailVerified: !!user.email_confirmed_at,

    // Profile info
    firstName,
    lastName,
    displayName,
    fullName,
    phoneNumber: profileData?.phone_number || null,
    photoUrl: profileData?.photo_url || null,
    companyId: profileData?.company_id || null,
    isActive: profileData?.is_active ?? true,
    webauthnEnabled: profileData?.webauthn_enabled ?? false,
    country_id: profileData?.country_id || null,
    country_name: profileData?.country_name || null,
    country_emoji: profileData?.country_emoji || null,
    country_dialing_code: profileData?.country_dialing_code || null,
    profileUrl: profileData?.photo_url || null,

    // Preferences
    preferences: profileData?.preferences || {
      expiry: 'one-hour',
      theme: 'system',
    },

    // Role info
    roles,
    primaryRole,
    permissions,

    // Company Info
    companyName: companyName || undefined,
    companyLogoUrl: companyLogoUrl || undefined,

    // Helper methods
    hasRole,
    hasPermission,
    isAdmin:
      hasRole('super_admin') ||
      hasRole('internal_admin') ||
      hasRole('company_admin'),
    isInternalAdmin: hasRole('internal_admin') || hasRole('super_admin'),
    isSuperAdmin: hasRole('super_admin'),
    isCompanyAdmin: hasRole('company_admin'),
    isEndUser: primaryRole === 'end_user',
    isDataAdministrator: hasRole('data_administrator'),
    isViewer: hasRole('viewer'),
  };
}

/**
 * Hook to get just the user's display name (lightweight)
 */
export function useUserDisplayName(): string {
  const { profile, isLoading } = useUserProfile();

  if (isLoading) return '';
  return profile?.fullName || '';
}

/**
 * Hook to check if user has a specific role
 */
export function useHasRole(role: AppRole): boolean {
  const { profile } = useUserProfile();
  return profile?.hasRole(role) ?? false;
}

/**
 * Hook to check if user has a specific permission
 */
export function useHasPermission(permission: keyof RolePermissions): boolean {
  const { profile } = useUserProfile();
  return profile?.hasPermission(permission) ?? false;
}
